import { VirtualMouseEvents } from "@/hooks/use_virtual_mouse"
import { KCSDK, MouseEventData, MouseEventType } from "@/sdk"
import { OnCursorChange } from "@/sdk/cursor_event_processor"
import { useAppDispatch, useAppSelector } from "@/store"
import { selectIsChromiumBlinkBrowser } from "@/stores/app_slice"
import { updateMousePos } from "@/stores/mouse_slice"
import {
  selectIsFullScreen,
  selectMouseScrollSpeed,
  selectIsThreeDMouse,
} from "@/stores/remote_desk_state_slice"

import { OS, getOS } from "@/utils/os"
import { useEffect, useMemo, useRef } from "react"
import { ConnectionUseCase } from "./connection"
import { mytest } from "@/utils/tools"

interface Param {
  // 视频的缩放比例
  scale: number
  // 视频组件的偏移量
  left: number
  top: number
}

type MouseEventHandler = (event: MouseEvent) => void

interface Result {
  onMouseEnter: MouseEventHandler
  onMouseLeave: MouseEventHandler
  onMouseMove: MouseEventHandler
  onMouseDown: MouseEventHandler
  onMouseUp: MouseEventHandler
  onWheel: (event: WheelEvent) => void
  onClick: MouseEventHandler
}

const WHEEL_STEP = 160
const WHEEL_MIN_INTERVAL_MS = 16
const WHEEL_DEADZONE = 4
const WHEEL_LINE_HEIGHT = 40
const DEFAULT_WHEEL_SPEED = 0.25
const WHEEL_MAX_FACTOR = 10
const WHEEL_MAX_PACKET_VALUE = 127
const WHEEL_PACKET_INTERVAL_MS = 5

function getWheelSpeedFactor(ratio: number): number {
  const normalized = Math.max(0, Math.min(1, ratio))
  const factor = 1 + (WHEEL_MAX_FACTOR - 1) * normalized
  const defaultFactor = 1 + (WHEEL_MAX_FACTOR - 1) * DEFAULT_WHEEL_SPEED
  return factor / defaultFactor
}

function normalizeWheelDeltaY(event: WheelEvent): number {
  switch (event.deltaMode) {
    case WheelEvent.DOM_DELTA_LINE:
      return event.deltaY * WHEEL_LINE_HEIGHT
    case WheelEvent.DOM_DELTA_PAGE:
      return event.deltaY * window.innerHeight
    case WheelEvent.DOM_DELTA_PIXEL:
    default:
      return event.deltaY
  }
}

// 用于处理鼠标事件
export const useRemoteDeskMouse = ({ scale, left, top }: Param): Result => {
  // 鼠标当前按下的按键
  const pressedButton = useRef(0)
  // 上次 onMouseMove 事件的时间戳
  const lastMouseMoveTime = useRef<number>(0)
  const lastWheelSendTime = useRef<number>(0)
  const lastWheelDirection = useRef<number>(0)
  const wheelEventQueue = useRef<MouseEventData[]>([])
  const wheelQueueTimer = useRef<number | null>(null)
  const isProcessingWheelQueue = useRef(false)
  // 全屏模式下的累积鼠标位置
  const fullScreenMousePos = useRef<{ newX: number; newY: number } | null>(null)
  // 跟踪最后一次的真实鼠标位置（用于进入全屏时的初始位置）
  const lastRealMousePos = useRef<{ x: number; y: number } | null>(null)
  // 跟踪最后一次的客户端鼠标位置（用于调试灵敏度对比）
  // const lastClientPos = useRef<{ x: number; y: number } | null>(null)
  const dispatch = useAppDispatch()
  const os = useMemo(() => getOS(), [])
  const isMac = getOS() === OS.MacOS
  const isChromiumBlinkVal = useAppSelector(selectIsChromiumBlinkBrowser)

  const mouseScrollSpeed = useAppSelector(selectMouseScrollSpeed)
  const mouseScrollSpeedRef = useRef(mouseScrollSpeed)
  mouseScrollSpeedRef.current = mouseScrollSpeed

  const isFullScreen = useAppSelector(selectIsFullScreen)
  const isThreeDMouse = useAppSelector(selectIsThreeDMouse)
  const shouldUseMacFullScreenVirtualMouse =
    isFullScreen && isMac && mytest && isChromiumBlinkVal

  useEffect(() => {
    // 屏蔽浏览器默认右键行为
    document.oncontextmenu = (e) => false

    const dispose = (): void => {
      // 恢复浏览器默认右键行为
      document.oncontextmenu = (e) => true
    }
    return dispose
  }, [])

  useEffect(() => {
    return () => {
      if (wheelQueueTimer.current !== null) {
        window.clearTimeout(wheelQueueTimer.current)
      }
      wheelEventQueue.current = []
      isProcessingWheelQueue.current = false
    }
  }, [])

  // 非全屏模式下，在 document 上监听鼠标移动，确保即使鼠标在悬浮球等 UI 元素上也能更新位置
  useEffect(() => {
    if (!isFullScreen) {
      const handleGlobalMouseMove = (event: MouseEvent) => {
        lastRealMousePos.current = { x: event.clientX, y: event.clientY }
      }
      document.addEventListener("mousemove", handleGlobalMouseMove)
      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove)
      }
    }
  }, [isFullScreen])

  // 当进入/退出全屏时，处理累积鼠标位置
  useEffect(() => {
    if (shouldUseMacFullScreenVirtualMouse) {
      // 进入全屏时，使用最后一次的真实鼠标位置作为初始位置
      const initX = lastRealMousePos.current?.x
      const initY = lastRealMousePos.current?.y

      fullScreenMousePos.current = { newX: initX!, newY: initY! }
      dispatch(updateMousePos({ x: initX!, y: initY! }))
    } else {
      // 退出全屏时，重置累积鼠标位置
      fullScreenMousePos.current = null
      // 清理虚拟鼠标事件状态
      VirtualMouseEvents.clearState()
    }
  }, [shouldUseMacFullScreenVirtualMouse, dispatch])

  // 计算全屏模式下的累积鼠标位置
  const fullScreenMousePosHandle = (
    event: MouseEvent
  ): { fullScreenX: number; fullScreenY: number } => {
    // 计算累积鼠标位置
    let fullScreenX: number
    let fullScreenY: number
    if (fullScreenMousePos.current === null) {
      // 第一次：使用 clientX 和 clientY 作为初始位置
      fullScreenX = event.clientX
      fullScreenY = event.clientY
    } else {
      // 之后每次：在累积位置上加上 movementX 和 movementY，并应用灵敏度系数
      fullScreenX = fullScreenMousePos.current.newX + event.movementX * 0.55
      fullScreenY = fullScreenMousePos.current.newY + event.movementY * 0.55
    }

    // if (isThreeDMouse) {
    //   const initX = lastRealMousePos.current?.x
    //   const initY = lastRealMousePos.current?.y
    //   // 更新累积位置
    //   fullScreenMousePos.current = { newX: initX!, newY: initY! }

    //   return { fullScreenX: initX!, fullScreenY: initY! }
    // } else {
    // 将 fullScreenX, fullScreenY 限制在屏幕范围内
    fullScreenX = Math.max(0, Math.min(fullScreenX, window.innerWidth))
    fullScreenY = Math.max(0, Math.min(fullScreenY, window.innerHeight))

    // 更新累积位置
    fullScreenMousePos.current = { newX: fullScreenX, newY: fullScreenY }

    return { fullScreenX, fullScreenY }
    // }
  }

  const releaseMouseUp = (
    eventType: MouseEventType,
    event: MouseEvent
  ): void => {
    const upBtn = event.buttons ^ pressedButton.current
    processMouseData(eventType, event, upBtn)
    pressedButton.current = event.buttons
  }

  const onMouseEnter = (event: MouseEvent): void => {
    if (pressedButton.current !== 0) {
      releaseMouseUp(MouseEventType.up, event)
    }
  }

  const onMouseLeave = (event: MouseEvent): void => {
    // 清空鼠标本地窗口坐标信息
    dispatch(updateMousePos(null))

    if (pressedButton.current !== 0) {
      releaseMouseUp(MouseEventType.up, event)
    }
  }

  const onMouseMove = (event: MouseEvent): void => {
    // 调试：计算 clientX/clientY 差值与 movementX/movementY 的对比
    // if (lastClientPos.current !== null) {
    //   const deltaX = event.clientX - lastClientPos.current.x
    //   const deltaY = event.clientY - lastClientPos.current.y
    //   const movementX = event.movementX
    //   const movementY = event.movementY

    //   // 只在有实际移动时打印（避免静止时的噪音）
    //   if (deltaX !== 0 || deltaY !== 0 || movementX !== 0 || movementY !== 0) {
    //     const ratioX = movementX !== 0 ? deltaX / movementX : 0
    //     const ratioY = movementY !== 0 ? deltaY / movementY : 0

    //     console.log(
    //       "[mousePos] 灵敏度对比 - " +
    //         `deltaX: ${deltaX}, movementX: ${movementX},ratioX: ${ratioX.toFixed(3)} | ` +
    //         `deltaY: ${deltaY}, movementY: ${movementY}, ratioY: ${ratioY.toFixed(3)}`
    //     )
    //   }
    // }
    // lastClientPos.current = { x: event.clientX, y: event.clientY }
    // 计算与上次 onMouseMove 的时间间隔
    const now = performance.now()
    const interval =
      lastMouseMoveTime.current > 0 ? now - lastMouseMoveTime.current : Infinity
    // 间隔超过 10ms 才发送移动事件
    const shouldSendMoveEvent = interval >= 10

    if (shouldSendMoveEvent) {
      lastMouseMoveTime.current = now
    }

    // 鼠标在云桌面中的坐标
    if (shouldUseMacFullScreenVirtualMouse) {
      const { fullScreenX, fullScreenY } = fullScreenMousePosHandle(event)
      dispatch(
        updateMousePos({
          x: fullScreenX,
          y: fullScreenY,
        })
      )

      // 检查虚拟鼠标是否在云桌面有效区域内
      const position = getPositionInDesk(event, fullScreenX, fullScreenY)

      // 派发虚拟鼠标事件，返回是否命中 UI 元素及进入/离开状态
      const { isOnUI, justEntered, justLeave } =
        VirtualMouseEvents.dispatchMove(fullScreenX, fullScreenY)

      if (justLeave) {
        // 从非 UI 区域进入 UI 区域，触发 "离开远程桌面" 逻辑
        console.log("[mousePos] 离开远程桌面，进入 UI 元素区域")
        if (pressedButton.current !== 0) {
          releaseMouseUp(MouseEventType.up, event)
        }
      } else if (justEntered) {
        // 从 UI 区域离开到非 UI 区域，触发 "进入远程桌面" 逻辑
        console.log("[mousePos] 进入远程桌面，离开 UI 元素区域")
        if (pressedButton.current !== 0) {
          releaseMouseUp(MouseEventType.up, event)
        }
      }

      // 只有当鼠标不在 UI 元素上且在云桌面有效区域内时，才发送鼠标数据到远程桌面
      if (!isOnUI && shouldSendMoveEvent && position.isMouseIn) {
        processMouseData(MouseEventType.move, event)
      }

      // 超出桌面范围时上报鼠标抬起事件 并 清空鼠标本地窗口坐标信息
      if (!position.isMouseIn) {
        if (pressedButton.current !== 0) {
          // 超出桌面范围时上报鼠标抬起事件
          releaseMouseUp(MouseEventType.up, event)
        }
      }
    } else {
      // 非全屏模式：记录真实鼠标位置，用于进入全屏时的初始位置
      lastRealMousePos.current = { x: event.clientX, y: event.clientY }

      const position = getPositionInDesk(event)
      if (position.isMouseIn) {
        // 更新鼠标本地窗口坐标信息
        dispatch(updateMousePos({ x: event.clientX, y: event.clientY }))

        // 上报鼠标移动事件（间隔超过 10ms 才发送）
        if (shouldSendMoveEvent) {
          processMouseData(MouseEventType.move, event)
        }
      } else {
        // 清空鼠标本地窗口坐标信息
        dispatch(updateMousePos(null))

        if (pressedButton.current !== 0) {
          // 超出桌面范围时上报鼠标抬起事件
          releaseMouseUp(MouseEventType.up, event)
        }
      }
    }
  }

  const onMouseDown = (event: MouseEvent): void => {
    if (shouldUseMacFullScreenVirtualMouse) {
      const { fullScreenX, fullScreenY } = fullScreenMousePosHandle(event)
      dispatch(
        updateMousePos({
          x: fullScreenX,
          y: fullScreenY,
        })
      )

      // 检查虚拟鼠标是否在云桌面有效区域内
      const position = getPositionInDesk(event, fullScreenX, fullScreenY)

      // 派发虚拟鼠标事件，返回是否命中 UI 元素
      const isOnUIElement = VirtualMouseEvents.dispatchDown(
        fullScreenX,
        fullScreenY,
        event.button
      )

      if (isOnUIElement) {
        // 阻止原生事件传播，防止触发 useOutsideClick 等钩子导致下拉菜单等组件关闭
        event.stopPropagation()
        event.stopImmediatePropagation()
      } else if (position.isMouseIn) {
        // 只有当鼠标不在 UI 元素上且在云桌面有效区域内时，才发送鼠标数据到远程桌面
        releaseMouseUp(MouseEventType.down, event)
      }
    } else {
      const position = getPositionInDesk(event)

      if (position.isMouseIn) {
        releaseMouseUp(MouseEventType.down, event)
      }
    }
  }

  const onMouseUp = (event: MouseEvent): void => {
    if (shouldUseMacFullScreenVirtualMouse) {
      const { fullScreenX, fullScreenY } = fullScreenMousePosHandle(event)
      dispatch(
        updateMousePos({
          x: fullScreenX,
          y: fullScreenY,
        })
      )

      // 检查虚拟鼠标是否在云桌面有效区域内
      const position = getPositionInDesk(event, fullScreenX, fullScreenY)

      // 派发虚拟鼠标事件，返回是否命中 UI 元素
      const isOnUIElement = VirtualMouseEvents.dispatchUp(
        fullScreenX,
        fullScreenY,
        event.button
      )

      if (isOnUIElement) {
        // 阻止原生事件传播，防止触发 useOutsideClick 等钩子
        event.stopPropagation()
        event.stopImmediatePropagation()
      } else if (position.isMouseIn) {
        // 只有当鼠标不在 UI 元素上且在云桌面有效区域内时，才发送鼠标数据到远程桌面
        releaseMouseUp(MouseEventType.up, event)
      }
    } else {
      const position = getPositionInDesk(event)
      if (position.isMouseIn) {
        releaseMouseUp(MouseEventType.up, event)
      }
    }
  }

  const onWheel = (event: WheelEvent): void => {
    if (shouldUseMacFullScreenVirtualMouse) {
      const { fullScreenX, fullScreenY } = fullScreenMousePosHandle(event)

      // 检查虚拟鼠标是否在云桌面有效区域内
      const position = getPositionInDesk(event, fullScreenX, fullScreenY)

      // 派发虚拟滚轮事件，返回是否在 UI 元素上
      // 传入 isUseMacMouseWheelDir 来控制滚动方向翻转
      const isOnUIElement = VirtualMouseEvents.dispatchWheel(
        fullScreenX,
        fullScreenY,
        event.deltaX,
        event.deltaY
      )
      // 只有当鼠标不在 UI 元素上且在云桌面有效区域内时，才发送滚轮数据到远程桌面
      if (!isOnUIElement && position.isMouseIn) {
        processMouseData(MouseEventType.wheel, event)
      }
    } else {
      const position = getPositionInDesk(event)
      if (position.isMouseIn) {
        processMouseData(MouseEventType.wheel, event)
      } else {
        event.preventDefault()
      }
    }
  }

  // 处理原生 click 事件（用于拦截虚拟鼠标点击 UI 后的原生 click 事件）
  const onClick = (event: MouseEvent): void => {
    if (shouldUseMacFullScreenVirtualMouse) {
      // 检查是否应该阻止这个原生 click 事件
      // 这发生在虚拟鼠标已经触发了 UI 元素的点击之后
      if (VirtualMouseEvents.checkAndResetBlockClick()) {
        event.stopPropagation()
        event.stopImmediatePropagation()
        event.preventDefault()
      }
    }
  }

  const processMouseData = (
    eventType: MouseEventType,
    event: MouseEvent,
    buttons?: number
  ): void => {
    if (!ConnectionUseCase.isPeerConnected()) {
      return
    }

    const threeDX = event.movementX
    const threeDY = event.movementY

    let x: number
    let y: number
    if (shouldUseMacFullScreenVirtualMouse) {
      const { fullScreenX, fullScreenY } = fullScreenMousePosHandle(event)
      const position = getPositionInDesk(event, fullScreenX, fullScreenY)
      x = position.x
      y = position.y
    } else {
      const position = getPositionInDesk(event)
      x = position.x
      y = position.y
    }

    event.preventDefault()

    // buttons： 左键：1，中键：4，右键：2，移动/滚动鼠标：undefined
    // buttonNum： 左键：1，中键：4，右键：2，移动/滚动鼠标：0
    let buttonNum = buttons ?? 0

    if (eventType === MouseEventType.wheel) {
      const wheelEvent = event as WheelEvent
      const speedFactor = getWheelSpeedFactor(mouseScrollSpeedRef.current)

      if (os === OS.MacOS) {
        // 触控板保留连续 delta；符号与本机自然滚动方向一致。
        buttonNum = Math.round(-wheelEvent.deltaY * speedFactor)
      } else {
        const normalizedDeltaY = normalizeWheelDeltaY(wheelEvent)
        if (Math.abs(normalizedDeltaY) < WHEEL_DEADZONE) {
          return
        }

        const direction = normalizedDeltaY > 0 ? 1 : -1
        const now = performance.now()
        const isSameDirection = direction === lastWheelDirection.current
        if (
          isSameDirection &&
          now - lastWheelSendTime.current < WHEEL_MIN_INTERVAL_MS
        ) {
          return
        }

        lastWheelDirection.current = direction
        lastWheelSendTime.current = now
        // Windows 鼠标和触控板都按本机物理方向映射到远端。
        const scaledStep = Math.max(1, Math.round(WHEEL_STEP * speedFactor))
        buttonNum = direction > 0 ? -scaledStep : scaledStep
      }
      // else {
      //   buttonNum = -wheelEvent.deltaY
      // }
    } else if (eventType === MouseEventType.move) {
      buttonNum = 0
    }

    const mouseData: MouseEventData = {
      event: eventType,
      x: isThreeDMouse ? threeDX : x,
      y: isThreeDMouse ? threeDY : y,
      buttonState: buttonNum,
      keyboardState: isThreeDMouse ? 1 : 0, //  1:3d鼠标   0：正常鼠标
    }

    if (eventType === MouseEventType.wheel) {
      enqueueWheelData(mouseData)
    } else {
      KCSDK.sendMouseData(mouseData)
    }
  }

  const processWheelQueue = (): void => {
    const mouseData = wheelEventQueue.current.shift()
    if (mouseData === undefined) {
      isProcessingWheelQueue.current = false
      wheelQueueTimer.current = null
      return
    }
    KCSDK.sendMouseData(mouseData)
    wheelQueueTimer.current = window.setTimeout(
      processWheelQueue,
      WHEEL_PACKET_INTERVAL_MS
    )
  }

  const enqueueWheelData = (mouseData: MouseEventData): void => {
    const total = Math.round(mouseData.buttonState)
    if (total === 0) return
    const direction = total > 0 ? 1 : -1
    let remaining = Math.abs(total)
    while (remaining > 0) {
      const current = Math.min(remaining, WHEEL_MAX_PACKET_VALUE)
      wheelEventQueue.current.push({
        ...mouseData,
        buttonState: direction * current,
      })
      remaining -= current
    }
    if (!isProcessingWheelQueue.current) {
      isProcessingWheelQueue.current = true
      processWheelQueue()
    }
  }

  // 非全屏/全屏模式下：计算鼠标在云桌面中的坐标，如果鼠标不在桌面中，返回对应的桌面边缘的位置
  const getPositionInDesk = (
    event: MouseEvent,
    fullScreenX?: number,
    fullScreenY?: number
  ): DeskPosition => {
    let x: number
    let y: number
    if (shouldUseMacFullScreenVirtualMouse) {
      x = fullScreenX ?? 0
      y = fullScreenY ?? 0
    } else {
      x = event.clientX
      y = event.clientY
    }

    const right = window.innerWidth - left
    const bottom = window.innerHeight - top
    let isMouseIn = false
    if (x < left) {
      x = 0
    } else if (x > right) {
      x = right
    } else if (y < top) {
      y = top
    } else if (y > bottom) {
      y = bottom
    } else {
      isMouseIn = true
      x = (x - left) * scale
      y = (y - top) * scale
    }

    return {
      x,
      y,
      isMouseIn,
    }
  }

  return {
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onMouseUp,
    onWheel,
    onClick,
  }
}

// 桌面坐标位置
interface DeskPosition {
  x: number
  y: number
  // 鼠标是否在桌面中
  isMouseIn: boolean
}

function onRemoteCursor(cursor: OnCursorChange) {
  KCSDK.setOnCursorChange(cursor)
}

export const MouseUseCase = {
  onRemoteCursor,
}
